import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { contactInfo } from '../data';

export default function Contact() {
  return (
    <div className="bg-white min-h-screen">
      <SEO />

      {/* Hero Header */}
      <section className="relative py-24 bg-stone-900 text-white">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214057.jpg" 
            alt="Kontakt os" 
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center flex flex-col gap-4">
          <span className="text-white font-semibold tracking-[0.2em] uppercase text-xs">
            Tag fat i os
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
            Kontakt os
          </h1>
          <p className="text-stone-300 text-lg max-w-2xl mx-auto">
            Har du spørgsmål eller ønsker du et uforpligtende tilbud? Vi står klar til at hjælpe dig.
          </p>
        </div>
      </section>

      {/* Contact Content Grid */}
      <section className="py-24 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Info Column */}
        <div className="lg:col-span-5 flex flex-col items-start gap-8">
          <div className="flex flex-col gap-3">
            <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
              Munkgaard Træbyg ApS
            </span>
            <h2 className="font-display text-3xl font-bold text-stone-900 leading-tight">
              Lad os tage en snak om dit næste projekt
            </h2>
            <p className="text-stone-600 leading-relaxed">
              Uanset om du overvejer et nyt sommerhus, en tilbygning, en totalrenovering eller mindre tømreropgaver, er du altid velkommen til at kontakte os. Vi beser gerne dit projekt og udarbejder et uforpligtende tilbud.
            </p>
          </div>

          {/* Contact Person Card */}
          <div className="w-full bg-stone-50 rounded-2xl border border-stone-200/60 p-5 flex items-center gap-5 shadow-sm">
            <div className="relative w-20 h-20 rounded-xl overflow-hidden shrink-0 border border-stone-200 shadow-sm">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1675582914353_db7a75c8.jpg" 
                alt="Jacob Munkgaard Sørensen" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-bold text-accent uppercase tracking-wider">Direkte kontakt</span>
              <h3 className="font-display text-lg font-bold text-stone-900">Jacob Munkgaard Sørensen</h3>
              <p className="text-xs text-stone-500">Tømrermester & Indehaver</p>
            </div>
          </div>

          {/* Contact Cards */}
          <div className="flex flex-col gap-4 w-full">
            <a 
              href={contactInfo.mapsUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl border border-stone-200/60 hover:border-accent hover:bg-white transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-xl shadow-sm group-hover:bg-accent group-hover:text-white transition-all duration-300">
                <i className="fa-solid fa-location-dot" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-stone-900 text-sm">Adresse</span>
                <span className="text-stone-600 text-sm mt-1">{contactInfo.address}</span>
              </div>
            </a>

            <a 
              href={`tel:${contactInfo.phoneRaw}`} 
              className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl border border-stone-200/60 hover:border-accent hover:bg-white transition-all duration-300 group whitespace-nowrap"
            >
              <div className="w-12 h-12 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-xl shadow-sm group-hover:bg-accent group-hover:text-white transition-all duration-300">
                <i className="fa-solid fa-phone" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-stone-900 text-sm">Telefon</span>
                <span className="text-stone-600 text-sm mt-1">{contactInfo.phone}</span>
              </div>
            </a>

            <a 
              href={`mailto:${contactInfo.email}`} 
              className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl border border-stone-200/60 hover:border-accent hover:bg-white transition-all duration-300 group whitespace-nowrap"
            >
              <div className="w-12 h-12 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-xl shadow-sm group-hover:bg-accent group-hover:text-white transition-all duration-300">
                <i className="fa-solid fa-envelope" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-stone-900 text-sm">E-mail</span>
                <span className="text-stone-600 text-sm mt-1">{contactInfo.email}</span>
              </div>
            </a>

            <div className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl border border-stone-200/60 group">
              <div className="w-12 h-12 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-accent text-xl shadow-sm">
                <i className="fa-solid fa-id-card" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-stone-900 text-sm">Virksomhedsinformation</span>
                <span className="text-stone-600 text-sm mt-1">CVR nr.: {contactInfo.cvr}</span>
              </div>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="flex flex-col gap-3 w-full border-t border-stone-100 pt-6">
            <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Følg os på sociale medier</span>
            <div className="flex items-center gap-3">
              <a 
                href={contactInfo.facebook} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-stone-100 hover:bg-accent hover:text-white text-stone-700 text-sm font-medium transition-all duration-300"
              >
                <i className="fa-brands fa-facebook text-lg" />
                <span>Facebook</span>
              </a>
              <a 
                href={contactInfo.instagram} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-stone-100 hover:bg-accent hover:text-white text-stone-700 text-sm font-medium transition-all duration-300"
              >
                <i className="fa-brands fa-instagram text-lg" />
                <span>Instagram</span>
              </a>
            </div>
          </div>
        </div>

        {/* Form Column */}
        <div className="lg:col-span-7">
          <ContactForm />
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[500px] border-t border-stone-200 relative">
        <iframe 
          title="Munkgaard Træbyg ApS på kort"
          src={contactInfo.mapsEmbedUrl}
          width="100%" 
          height="100%" 
          style={{ border: 0 }} 
          allowFullScreen="" 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
          className="grayscale contrast-115"
        />
      </section>
    </div>
  );
}
